import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MobileDrawer } from './MobileDrawer';
import { Sidebar, visibleNavItems } from './Sidebar';
import { PeriodFilter } from '../PeriodFilter';
import { ThemeToggle } from '../ThemeToggle';
import { UserMenu } from '../UserMenu';
import { Badge, CompanyLogo } from '../ui';
import { useCompany } from '../../lib/companyContext';

/**
 * Casca do painel (RNF16).
 *
 * Uma unica arvore atende os dois formatos:
 *  - a partir de `lg` a pagina vira uma grade de duas colunas e a lateral e uma
 *    coluna fixa que rola por conta propria;
 *  - abaixo disso a mesma Sidebar e servida dentro do MobileDrawer, sobreposta.
 *
 * O cabecalho carrega, a esquerda, a IDENTIDADE DA EMPRESA em foco (logo, nome
 * e estado) -- e o "nome da empresa em cima" do CRM de cada lojista -- e, a
 * direita, os tres controles que valem para TODAS as paginas: o filtro de
 * periodo (escopa tudo abaixo), o tema e o menu do usuario. Nenhum deles mora
 * dentro de um cartao -- filtro por cartao e a receita para numeros que nao
 * batem entre si.
 *
 * Lateral recolhivel (25/09/2026): a escolha fica no navegador (`cf.lateral`) e
 * vira o atributo `data-sidebar` do shell, que troca a variavel CSS
 * `--sidebar-width` lida por `grid-cols-shell`. Layout continua sendo do CSS.
 * Atalhos: Alt+1..9 abrem os itens do menu na ordem em que aparecem; Alt+0
 * recolhe/expande. Sao ignorados quando o foco esta num campo de texto.
 *
 * Nao existe duplicacao de menu nem breakpoint decidido em JavaScript para
 * escolher o que renderizar: o layout e do CSS. O unico uso de matchMedia aqui
 * e para FECHAR o drawer quando a tela cresce -- estado, nao layout.
 */

/** Espelha o breakpoint `lg` do Tailwind. Se um mudar, o outro muda junto. */
const DESKTOP_MEDIA_QUERY = '(min-width: 1024px)';
const DRAWER_ID = 'drawer-navegacao';
const SIDEBAR_STORAGE_KEY = 'cf.lateral';

function lerLateralRecolhida(): boolean {
  try {
    return window.localStorage.getItem(SIDEBAR_STORAGE_KEY) === 'recolhida';
  } catch {
    return false;
  }
}

function guardarLateral(recolhida: boolean): void {
  try {
    window.localStorage.setItem(SIDEBAR_STORAGE_KEY, recolhida ? 'recolhida' : 'aberta');
  } catch {
    // Armazenamento indisponivel nao derruba o painel.
  }
}

function focoEmCampo(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable;
}

interface AppShellProps {
  readonly children: ReactNode;
}

export function AppShell({ children }: AppShellProps): JSX.Element {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<boolean>(() => lerLateralRecolhida());
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { company, isPlatformAdmin } = useCompany();
  const areaAdmin = pathname.startsWith('/admin');

  const closeDrawer = useCallback((): void => {
    setDrawerOpen(false);
  }, []);

  const openDrawer = useCallback((): void => {
    setDrawerOpen(true);
  }, []);

  const toggleCollapse = useCallback((): void => {
    setCollapsed((atual) => {
      guardarLateral(!atual);
      return !atual;
    });
  }, []);

  useEffect(() => {
    setDrawerOpen(false);
  }, [pathname]);

  useEffect(() => {
    const media = window.matchMedia(DESKTOP_MEDIA_QUERY);
    const handleChange = (event: MediaQueryListEvent): void => {
      if (event.matches) {
        setDrawerOpen(false);
      }
    };
    media.addEventListener('change', handleChange);
    return () => {
      media.removeEventListener('change', handleChange);
    };
  }, []);

  // Atalhos de teclado do menu: Alt+1..9 navega; Alt+0 recolhe/expande.
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (!event.altKey || event.ctrlKey || event.metaKey || focoEmCampo(event.target)) {
        return;
      }
      if (event.key === '0') {
        event.preventDefault();
        toggleCollapse();
        return;
      }
      if (!/^[1-9]$/.test(event.key)) {
        return;
      }
      const alvo = visibleNavItems(isPlatformAdmin)[Number(event.key) - 1];
      if (alvo === undefined) {
        return;
      }
      event.preventDefault();
      navigate({ pathname: alvo.to, search: window.location.search });
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [isPlatformAdmin, navigate, toggleCollapse]);

  return (
    <div data-sidebar={collapsed ? 'collapsed' : 'expanded'} className="min-h-dvh bg-page text-ink lg:grid lg:grid-cols-shell">
      <a
        href="#conteudo-principal"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-skip focus:rounded-lg focus:bg-elevated focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:shadow-lg"
      >
        Pular para o conteúdo
      </a>

      <div className="hidden border-r border-line transition-[width] lg:sticky lg:top-0 lg:block lg:h-dvh">
        <Sidebar collapsed={collapsed} onToggleCollapse={toggleCollapse} />
      </div>

      <div className="flex min-h-dvh min-w-0 flex-col">
        <header className="sticky top-0 z-header flex items-center gap-3 border-b border-line bg-page/85 px-4 py-3 backdrop-blur sm:px-6 lg:px-8">
          <button
            type="button"
            onClick={openDrawer}
            aria-label="Abrir menu de navegação"
            aria-expanded={drawerOpen}
            aria-controls={DRAWER_ID}
            className="-ml-1 rounded-lg p-2 text-ink-muted transition-colors hover:bg-ink/5 hover:text-ink active:scale-95 lg:hidden"
          >
            <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" aria-hidden="true" focusable="false">
              <path d="M3.75 6.75h16.5M3.75 12h16.5M3.75 17.25h16.5" />
            </svg>
          </button>

          <div data-slot="identidade-da-empresa" className="flex min-w-0 items-center gap-2.5">
            {areaAdmin ? (
              <>
                <span aria-hidden="true" className="grid h-8 w-8 place-items-center rounded-lg bg-hero text-xs font-bold text-white shadow-soft">
                  CF
                </span>
                <div className="min-w-0 leading-tight">
                  <p className="truncate text-sm font-semibold text-ink">Converte Fácil</p>
                  <p className="hidden truncate text-xs text-ink-subtle sm:block">Administração da plataforma</p>
                </div>
              </>
            ) : (
              <>
                <CompanyLogo company={company} size="sm" />
                <div className="min-w-0 leading-tight">
                  <p className="truncate text-sm font-semibold text-ink">{company.name}</p>
                  <p className="hidden truncate text-xs text-ink-subtle sm:block">{company.segment === '' ? 'Painel da empresa' : company.segment}</p>
                </div>
                {isPlatformAdmin ? (
                  <Badge tone={company.status === 'ativa' ? 'good' : company.status === 'onboarding' ? 'warning' : 'neutral'} dot className="hidden md:inline-flex">
                    {company.status === 'ativa' ? 'Ativa' : company.status === 'onboarding' ? 'Em implantação' : 'Pausada'}
                  </Badge>
                ) : null}
              </>
            )}
          </div>

          <div data-slot="acoes-do-cabecalho" className="ml-auto flex items-center gap-2 sm:gap-3">
            <PeriodFilter />
            <div className="hidden sm:block">
              <ThemeToggle />
            </div>
            <UserMenu />
          </div>
        </header>

        <main id="conteudo-principal" tabIndex={-1} className="flex-1 px-4 py-6 outline-none sm:px-6 lg:px-8 lg:py-8">
          <div className="mx-auto w-full max-w-7xl">{children}</div>
        </main>
      </div>

      <MobileDrawer open={drawerOpen} onClose={closeDrawer} id={DRAWER_ID} label="Navegação principal">
        <Sidebar onNavigate={closeDrawer} />
      </MobileDrawer>
    </div>
  );
}
